import type {
  AxiosError,
  AxiosInstance,
  InternalAxiosRequestConfig,
} from "axios";
import { ApiError, type ApiErrorCode } from "@/lib/api/api-error";
import { notifyAdminSessionExpired } from "@/lib/auth/admin-session-expired";
import {
  getCurrentAdminAccessToken,
  refreshAdminAccessToken,
} from "@/lib/auth/refresh-admin-tokens";

type RetriableConfig = InternalAxiosRequestConfig & { _authRetry?: boolean };

function statusToCode(status: number | undefined): ApiErrorCode {
  switch (status) {
    case 400:
      return "validation_failed";
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 423:
      return "account_locked";
    case 429:
      return "rate_limited";
    case 500:
    case 502:
    case 503:
    case 504:
      return "server_error";
    default:
      return status === undefined ? "network_error" : "unknown";
  }
}

function toApiError(
  error: AxiosError<{
    message?: string;
    code?: ApiErrorCode;
    details?: unknown;
    detail?: unknown;
  }>,
): ApiError {
  const status = error.response?.status;
  const code = error.response?.data?.code ?? statusToCode(status);
  const message =
    error.response?.data?.message ??
    error.message ??
    "Unexpected error contacting admin API.";

  return new ApiError({
    code,
    status: status ?? 0,
    message,
    requestId: (error.response?.headers?.["x-request-id"] as string | undefined) ??
      undefined,
    // FastAPI wraps custom errors as { detail: {...} }. Expose that detail (the
    // gateway sets it to { code, reason, ... }) so callers can read the specific
    // reason. `details` was effectively always undefined for these, so this is
    // purely additive.
    details: error.response?.data?.details ?? error.response?.data?.detail,
  });
}

function isAuthEndpoint(url: string | undefined): boolean {
  if (!url) return false;
  return (
    url.includes("/admin/auth/login") ||
    url.includes("/admin/auth/mfa/verify") ||
    url.includes("/admin/auth/refresh") ||
    url.includes("/admin/auth/logout")
  );
}

function setAuthorizationHeader(
  config: InternalAxiosRequestConfig,
  token: string,
): void {
  const value = `Bearer ${token}`;
  if (typeof config.headers?.set === "function") {
    config.headers.set("Authorization", value);
    return;
  }
  (config.headers as Record<string, string>).Authorization = value;
}

/**
 * On 401: try refresh once, retry the original request, otherwise end the
 * session so the UI redirects to login instead of showing errors while
 * still "authenticated".
 */
export function attachAdminHttpAuthInterceptor(client: AxiosInstance): void {
  client.interceptors.response.use(
    (response) => response,
    async (
      error: AxiosError<{
        message?: string;
        code?: ApiErrorCode;
        details?: unknown;
      }>,
    ) => {
      const status = error.response?.status;
      const config = error.config as RetriableConfig | undefined;

      if (status === 401 && config && !isAuthEndpoint(config.url)) {
        if (config._authRetry) {
          notifyAdminSessionExpired();
          throw toApiError(error);
        }

        config._authRetry = true;

        const token =
          (await refreshAdminAccessToken()) ?? getCurrentAdminAccessToken();

        if (token) {
          setAuthorizationHeader(config, token);
          try {
            return await client.request(config);
          } catch (retryErr) {
            const retryStatus = (retryErr as AxiosError).response?.status;
            if (retryErr instanceof ApiError && retryErr.status === 401) {
              notifyAdminSessionExpired();
              throw retryErr;
            }
            if (retryStatus === 401) {
              notifyAdminSessionExpired();
            }
            throw retryErr instanceof ApiError ? retryErr : toApiError(error);
          }
        }

        notifyAdminSessionExpired();
      }

      throw toApiError(error);
    },
  );
}
